/**
 * Ephemeral state for an in-progress annotation **drag** (ADR-034 — geometry
 * move, 2026-06-11). The drag hook (:file:`@/map/use-annotation-drag.ts`) writes
 * the live geometry here on each mousemove; the render + label layers read it via
 * :func:`useAnnotationsWithDrag` so the shape *and* its readout follow the cursor.
 * Cleared on drop / cancel; the `PUT` refetch then supplies the persisted
 * geometry.
 *
 * Cross-references:
 *   - ADR-034 (D7 — geometry move); feature spec §8.
 */
import { useMemo } from "react";
import { create } from "zustand";
import { useAnnotations } from "@/annotations/use-annotations";
import type { Annotation } from "@/annotations/use-annotations";

type AnnotationGeometry = Annotation["geometry"];

/** The live drag — which annotation + its in-progress geometry. */
export interface AnnotationDrag {
  readonly annotationId: string;
  readonly geometry: AnnotationGeometry;
  /** In-progress radius for a circle RESIZE (REQ-UI-137). Undefined for every
   *  other gesture: a resize is the one edit that changes a shape without
   *  changing its geometry — the centre Point stays put and only `radius_m`
   *  moves — so the live preview needs this carried alongside. */
  readonly radiusM?: number | null;
}

interface AnnotationDragState {
  readonly drag: AnnotationDrag | null;
  /** Set/replace the live drag geometry (per mousemove). */
  readonly setDrag: (drag: AnnotationDrag) => void;
  /** Clear the live drag (on drop or cancel). */
  readonly clearDrag: () => void;
}

export const useAnnotationDragStore = create<AnnotationDragState>((set) => ({
  drag: null,
  setDrag: (drag) => {
    set({ drag });
  },
  clearDrag: () => {
    set({ drag: null });
  },
}));

/**
 * Return `annotations` with the dragged one's geometry replaced by the live drag
 * geometry (the list is returned unchanged when nothing is dragging). Pure — the
 * single live-preview substitution point used by every render builder.
 */
export function applyDragOverride(
  annotations: Annotation[] | undefined,
  drag: AnnotationDrag | null,
): Annotation[] | undefined {
  if (annotations === undefined || drag === null) {
    return annotations;
  }
  return annotations.map((annotation) =>
    annotation.annotation_id === drag.annotationId
      ? {
          ...annotation,
          geometry: drag.geometry,
          // Only override the radius while a resize is live; leaving it
          // undefined for a translate keeps the circle's own radius.
          ...(drag.radiusM != null ? { radius_m: drag.radiusM } : {}),
        }
      : annotation,
  );
}

/**
 * The annotations list with any in-progress drag applied — the render + label
 * layers consume this instead of :func:`useAnnotations` directly, so a dragged
 * shape (and its measurement readout) tracks the cursor before the `PUT` lands.
 */
export function useAnnotationsWithDrag(): Annotation[] | undefined {
  const data = useAnnotations().data;
  const drag = useAnnotationDragStore((state) => state.drag);
  return useMemo(() => applyDragOverride(data, drag), [data, drag]);
}
